import React, { useState } from 'react'
import {
  FormGroup,
  EditableText,
  H2,
  Spinner,
  Intent,
  Classes,
  Tag,
  SpinnerSize,
} from '@blueprintjs/core'
import { Tooltip2 } from '@blueprintjs/popover2'
import { IconButton, Toaster, Flex, Dialog, Confirmation } from '@ui'
import { NavigateFn } from '@reach/router'
import { handleErrors } from './TableView'
import api, { graphQLError, IBuildingTypeWithCompany } from '@api'
import { omit } from 'lodash'
import useBuildingType from './hooks/useBuildingType'
import track from '@track'
import Event from '@track/Event'

export default function BuildingTypeDetails({
  buildingTypeId,
  navigate,
  reload,
}: {
  buildingTypeId: string
  navigate?: NavigateFn
  reload?: () => void
}) {
  const { buildingType, isLoading, updateBuildingType } = useBuildingType(buildingTypeId)
  const [value, setValue] = useState<null | string>(null)
  const [errors, setErrors] = useState<{ [id: string]: React.ReactNode }>({})
  const [updating, setUpdating] = useState(false)

  const onClose = () => {
    navigate?.(window.location.pathname)
  }

  return (
    <Dialog
      isOpen
      title=""
      isCloseButtonShown={false}
      onClose={onClose}
      style={{ minWidth: 400 }}
      toolbar={
        <div className="dialog-buttons" style={{ display: 'flex' }}>
          {buildingType.company && (
            <Confirmation
              cancelable
              text={
                <div style={{ width: 200, whiteSpace: 'normal' }}>Delete this Building Type?</div>
              }
              onConfirm={() => {
                track.buildingTypeDialog({
                  eventName: Event.DELETE_RECORD_DIALOG,
                })
                api.building.buildingType
                  .remove(buildingType.id)
                  .then(() => {
                    reload?.()
                    Toaster.success({
                      message: 'Building Type Removed',
                    })
                    onClose()
                  })
                  .catch((error: { graphQLErrors: graphQLError[] }) => {
                    const stringErrors = handleErrors(error, false) as string[]
                    stringErrors.forEach((e: string) => Toaster.fail({ message: e }))
                  })
              }}
            >
              <IconButton minimal className="remove-icon" tooltip="Delete" iconName="trash" />
            </Confirmation>
          )}

          <IconButton minimal iconName="times" onClick={onClose} tooltip="Close" />
        </div>
      }
      body={
        <div className={Classes.DIALOG_BODY}>
          <H2 className={isLoading ? Classes.SKELETON : ''}>
            {buildingType.company ? (
              <FormGroup
                style={{
                  marginBottom: 'none',
                  marginRight: updating ? 20 : 0,
                }}
                helperText={errors[buildingTypeId]}
                intent={errors[buildingTypeId] ? Intent.DANGER : undefined}
              >
                <Flex align="center" justify="flex-start" style={{ width: '100%' }}>
                  <EditableText
                    multiline={false}
                    value={value || buildingType.displayName}
                    onChange={(newValue) => {
                      setValue(newValue)
                    }}
                    onConfirm={() => {
                      if (value && value !== buildingType.displayName) {
                        track.buildingTypeDialog({
                          eventName: Event.INLINE_ACTION,
                          props: {
                            actionName: 'Rename Building Type',
                            actionValue: null,
                          },
                        })
                        setUpdating(true)
                        api.building.buildingType
                          .update(buildingTypeId, value.trim())
                          .then((updatedBuildingType: IBuildingTypeWithCompany) => {
                            setUpdating(false)
                            setErrors(omit(setErrors, buildingTypeId))
                            reload?.()
                            updateBuildingType(updatedBuildingType)
                            setValue(null)
                            Toaster.success({
                              message: 'Building Type Updated',
                            })
                          })
                          .catch((error: { graphQLErrors: graphQLError[] }) => {
                            setUpdating(false)
                            setValue(buildingType.displayName)
                            setErrors({
                              ...errors,
                              [buildingTypeId]: handleErrors(error, true),
                            })
                          })
                      } else {
                        setValue(null)
                      }
                    }}
                  />
                  {updating && (
                    <div style={{ width: 20, marginLeft: 10 }}>
                      <Spinner size={SpinnerSize.SMALL} />
                    </div>
                  )}
                </Flex>
              </FormGroup>
            ) : (
              buildingType.displayName
            )}
          </H2>
          {!buildingType.company && (
            <Tooltip2 content="Default Building Types cannot be edited or removed">
              <Tag minimal className="ml-1 cursor-default">
                Default
              </Tag>
            </Tooltip2>
          )}
        </div>
      }
    />
  )
}
